import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { Database, Loader2 } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const GoogleIcon = ({ testId }) => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-testid={testId}>
    <path
      fill="#EA4335"
      d="M12 10.2v3.95h5.52c-.22 1.19-.88 2.2-1.88 2.88v2.39h3.05c1.78-1.64 2.8-4.05 2.8-6.94 0-.67-.07-1.31-.2-1.93H12z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.43 0 4.47-.8 5.96-2.16l-3.05-2.39c-.85.58-1.95.92-2.91.92-2.24 0-4.14-1.51-4.82-3.54H4.03v2.46C5.5 21.1 8.5 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M7.18 15.83a5.5 5.5 0 0 1 0-3.66V9.7H4.03a10.9 10.9 0 0 0 0 4.6l3.15-2.47z"
    />
    <path
      fill="#4285F4"
      d="M12 5.1c1.32 0 2.5.45 3.44 1.34l2.58-2.58C16.46 2.35 14.43 1.5 12 1.5 8.5 1.5 5.5 3.4 4.03 6.23l3.15 2.47C7.86 6.61 9.76 5.1 12 5.1z"
    />
  </svg>
);

const RegisterPage = () => {
  const { register, loginWithGoogle } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [siteName, setSiteName] = useState("AIQueryStudio");

  useEffect(() => {
    axios.get(`${API_URL}/settings/public`)
      .then(r => { if (r.data?.site_name) setSiteName(r.data.site_name); })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created successfully!");
      // Track signup conversion
      if (window.trackEvent) {
        window.trackEvent('signup', 'conversion', 'email_registration', 1);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    // Track Google signup attempt
    if (window.trackEvent) {
      window.trackEvent('signup_attempt', 'conversion', 'google_sso');
    }
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-8">
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-6 h-6 text-primary" />
            <span className="font-bold text-lg">{siteName}</span>
          </div>
          <CardTitle className="text-2xl font-bold">Create an account</CardTitle>
          <CardDescription>Get started with {siteName}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Google SSO Button */}
          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2 h-11"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              data-testid="google-register-btn"
            >
              {googleLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <GoogleIcon testId="google-register-icon" />
              )}
              Continue with Google
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Google sign-in is available on desktop browsers only
            </p>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or register with email</span>
            </div>
          </div>

          {/* Email Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="register-confirm-password-input"
                className="bg-background"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create account"
              )}
            </Button>
          </form>
          <div className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
